import { FetchingFlagResolverClient } from './FlagResolverClient';
import { EventSenderEngine } from './EventSenderEngine';
import { Value } from './Value';
import { Logger } from './logger';
import { SdkId } from './generated/confidence/flags/resolver/v1/types';
import { Trackable } from './Trackable';
import { subject, changeObserver } from './observing';
import { AccessiblePromise } from './AccessiblePromise';
import { Telemetry } from './Telemetry';
import { FlagCache } from './flag-cache';
import { utf8ToBase64 } from './utils';
/**
 * Class containing main Confidence APIs
 * @public
 */
export class Confidence {
    /** Internal Confidence configurations */
    config;
    parent;
    _context;
    contextChanged;
    /**
     * Emits Closers on context change
     * @internal */
    contextChanges;
    currentFlags;
    pendingFlags;
    flagStateSubject;
    /** @internal */
    constructor({ context = {}, ...config }, parent) {
        this.config = config;
        this.parent = parent;
        this._context = new Map(Object.entries(context));
        this.contextChanges = subject(observer => {
            let parentSubscription;
            if (parent) {
                parentSubscription = parent.contextChanges(keys => {
                    const visibleKeys = keys.filter(key => !this._context.has(key));
                    if (visibleKeys.length)
                        observer(visibleKeys);
                });
            }
            this.contextChanged = observer;
            return () => {
                parentSubscription?.();
                this.contextChanged = undefined;
            };
        });
        this.flagStateSubject = subject(observer => {
            const reportState = () => observer(this.flagState);
            if (!this.currentFlags || !Value.equal(this.currentFlags.context, this.getContext())) {
                this.resolveFlags().then(reportState);
            }
            const close = this.contextChanges(() => {
                if (this.flagState === 'READY' || this.flagState === 'ERROR')
                    observer('STALE');
                this.resolveFlags().then(reportState);
            });
            return () => {
                close();
                this.pendingFlags?.abort();
                this.pendingFlags = undefined;
            };
        });
    }
    /** Returns currently used environment */
    get environment() {
        return this.config.environment;
    }
    sendEvent(name, data) {
        this.config.eventSenderEngine.send(this.getContext(), name, data);
    }
    *contextEntries() {
        if (this.parent) {
            // all parent entries except the ones child also has
            for (const entry of this.parent.contextEntries()) {
                if (!this._context.has(entry[0])) {
                    yield entry;
                }
            }
        }
        // all child entries except undefined
        for (const entry of this._context.entries()) {
            if (typeof entry[1] !== 'undefined') {
                yield entry;
            }
        }
    }
    /** Returns context of the current Confidence instance */
    getContext() {
        const context = {};
        for (const [key, value] of this.contextEntries()) {
            context[key] = value;
        }
        return Object.freeze(context);
    }
    /** Set Confidence context */
    setContext(context) {
        const current = this.getContext();
        const changedKeys = [];
        for (const key of Object.keys(context)) {
            if (Value.equal(current[key], context[key]))
                continue;
            changedKeys.push(key);
            this._context.set(key, Value.clone(context[key]));
        }
        if (this.contextChanged && changedKeys.length > 0) {
            this.contextChanged(changedKeys);
        }
        return changedKeys.length > 0;
    }
    /** Clears context of current Confidence instance */
    clearContext() {
        const oldContext = this.getContext();
        this._context.clear();
        if (this.contextChanged) {
            const newContext = this.getContext();
            const unionKeys = Array.from(new Set([...Object.keys(oldContext), ...Object.keys(newContext)]));
            const changedKeys = unionKeys.filter(key => !Value.equal(oldContext[key], newContext[key]));
            if (changedKeys.length)
                this.contextChanged(changedKeys);
        }
    }
    /**
     * Creates a new Confidence instance with context
     * @param context - Confidence context
     * @returns Confidence instance
     */
    withContext(context) {
        const child = new Confidence(this.config, this);
        child.setContext(context);
        // child.resolveFlags();
        return child;
    }
    /**
     * Tracks an event
     * @param nameOrManager - event name of event manager
     * @param data - data to track
     * @returns - Closer
     */
    track(nameOrManager, data) {
        if (typeof nameOrManager === 'function') {
            return Trackable.setup(this, nameOrManager);
        }
        this.sendEvent(nameOrManager, data);
        return undefined;
    }
    /** Resolves all flags in cache */
    resolveFlags() {
        const context = this.getContext();
        if (!this.pendingFlags || !Value.equal(this.pendingFlags.context, context)) {
            this.pendingFlags?.abort();
            this.pendingFlags = this.config.flagResolverClient
                .resolve(context, [])
                .then(resolution => {
                this.currentFlags = resolution;
            })
                .catch(e => {
                // TODO fix sloppy handling of error
                if (e.name !== 'AbortError') {
                    this.config.logger.info?.('Resolve failed.', e);
                }
            })
                .finally(() => {
                // if this resolves synchronously, the assignment on 171 will actually happen after we clear it.
                this.pendingFlags = undefined;
            });
        }
        if (this.pendingFlags.status !== 'pending') {
            this.pendingFlags = undefined;
            return AccessiblePromise.resolve();
        }
        return this.pendingFlags;
    }
    /**
     * Shows flag state
     * @returns flag state - READY, NOT_READY, STALE or ERROR
     */
    get flagState() {
        if (this.currentFlags) {
            if (this.pendingFlags)
                return 'STALE';
            return this.currentFlags.state;
        }
        return 'NOT_READY';
    }
    /** Subscribe to flag changes in Confidence */
    subscribe(onStateChange = () => { }) {
        const observer = changeObserver(onStateChange);
        const close = this.flagStateSubject(observer);
        observer(this.flagState);
        return close;
    }
    evaluateFlagAsync(path, defaultValue) {
        let close;
        return new Promise(resolve => {
            close = this.subscribe(state => {
                // when state is ready we can be sure currentFlags exist
                if (state === 'READY' || state === 'ERROR') {
                    resolve(this.currentFlags.evaluate(path, defaultValue));
                }
            });
        }).finally(close);
    }
    evaluateFlag(path, defaultValue) {
        let evaluation;
        // resolveFlags might update state synchronously
        if (!this.currentFlags && !this.pendingFlags)
            this.resolveFlags();
        if (!this.currentFlags) {
            evaluation = {
                reason: 'ERROR',
                errorCode: 'NOT_READY',
                errorMessage: 'Flags are not yet ready',
                value: defaultValue,
            };
        }
        else {
            this.showLoggerLink(path, this.getContext());
            evaluation = this.currentFlags.evaluate(path, defaultValue);
        }
        if (!this.currentFlags || !Value.equal(this.currentFlags.context, this.getContext())) {
            const then = (onfulfilled, onrejected) => this.evaluateFlagAsync(path, defaultValue).then(onfulfilled, onrejected);
            const staleEvaluation = {
                ...evaluation,
                then,
            };
            return staleEvaluation;
        }
        return evaluation;
    }
    async getFlag(path, defaultValue) {
        return (await this.evaluateFlag(path, defaultValue)).value;
    }
    showLoggerLink(flag, context) {
        const data = {
            clientKey: this.config.clientSecret,
            flag: `flags/${flag}`,
            context,
        };
        const jsonString = JSON.stringify(data);
        const base64 = typeof window !== 'undefined' ? utf8ToBase64(jsonString) : Buffer.from(jsonString).toString('base64');
        this.config.logger.info?.(`Check your flag evaluation for '${flag}' by copy-pasting the payload to the Resolve tester: ${base64}`);
    }
    toOptions() {
        const cache = this.config.cacheProvider(this.config.clientSecret).toOptions();
        return {
            clientSecret: this.config.clientSecret,
            region: this.config.region,
            timeout: this.config.timeout,
            environment: this.config.environment,
            cache,
            context: this.getContext(),
        };
    }
    /**
     * Creates a Confidence instance
     * @param clientSecret - clientSecret found on the Confidence console
     * @param region - region in which Confidence will operate
     * @param timeout - timeout for flag resolves
     * @param environment - can be either "client" or "backend"
     * @param fetchImplementation - fetch implementation
     * @param logger - debug logger
     * @param resolveBaseUrl - custom backend resolve URL
     * @returns
     */
    static create(options) {
        const { clientSecret, region, timeout, environment, fetchImplementation = defaultFetchImplementation(), logger = defaultLogger(), resolveBaseUrl, disableTelemetry = false, applyDebounce = 10, waitUntil, cache = {}, } = options;
        if (environment !== 'client' && environment !== 'backend') {
            throw new Error(`Invalid environment: ${environment}. Must be 'client' or 'backend'.`);
        }
        const sdk = {
            id: SdkId.SDK_ID_JS_CONFIDENCE,
            version: '0.3.5', // x-release-please-version
        };
        const telemetry = new Telemetry({
            disabled: disableTelemetry,
            environment,
        });
        if (!clientSecret) {
            logger.error?.(`Confidence: confidence cannot be instantiated without a client secret`);
        }
        const cacheProvider = FlagCache.provider(clientSecret, cache);
        const flagResolverClient = new FetchingFlagResolverClient({
            clientSecret,
            fetchImplementation,
            sdk,
            environment,
            resolveTimeout: timeout,
            region,
            resolveBaseUrl,
            telemetry,
            logger,
            applyDebounce,
            waitUntil,
            cacheProvider,
        });
        const estEventSizeKb = 1;
        const flushTimeoutMilliseconds = 500;
        // default grpc payload limit is 4MB, so we aim for a 1MB batch-size
        const maxBatchSize = Math.floor(1024 / estEventSizeKb);
        const eventSenderEngine = new EventSenderEngine({
            clientSecret,
            maxBatchSize,
            flushTimeoutMilliseconds,
            fetchImplementation,
            region,
            // we set rate limit to support the flushTimeout
            // on backend, the rate limit would be ∞
            rateLimitRps: environment === 'client' ? 1000 / flushTimeoutMilliseconds : Number.POSITIVE_INFINITY,
            // the request is queued or in flight in memory to be sent.
            // max memory consumption is 50MB
            maxOpenRequests: (50 * 1024) / (estEventSizeKb * maxBatchSize),
            logger,
        });
        return new Confidence({
            ...options,
            flagResolverClient,
            eventSenderEngine,
            logger,
            cacheProvider,
        });
    }
}
function defaultFetchImplementation() {
    if (!globalThis.fetch) {
        throw new TypeError('No default fetch implementation found. Please provide provide the fetchImplementation option to createConfidenceWebProvider.');
    }
    return globalThis.fetch.bind(globalThis);
}
function defaultLogger() {
    try {
        if (process.env.NODE_ENV === 'development') {
            return Logger.withLevel(console, 'info');
        }
    }
    catch (e) {
        // ignore
    }
    return Logger.noOp();
}
//# sourceMappingURL=Confidence.js.map